import React, { useState } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import useApiClient from '../../hooks/api/useApiClient';
import { firstApiError } from '../../services/helpers/ApiErrors';
import { withPaymentReturn } from '../../views/restaurant-onboarding/phases/payoutsPhaseHelpers';

/**
 * Sends a partner (white-label) restaurant to its provider's hosted onboarding page
 * — the one KYC path those restaurants have (WLP Payments 5/8). The link is minted
 * on every click: Stripe's account links are single-use and expire within minutes,
 * and the backend recreates a missing provider account on the spot.
 *
 * Master restaurants keep OnboardingLinkButton, which reads the per-gateway
 * onboarding links those restaurants predate gateway connections with.
 */
export default function HostedOnboardingButton({
  gateway,
  type = 'default',
  className,
  errorMessage,
  returnTo = null,
  children,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Buttons:HostedOnboardingButton' });
  const { CompanyPaymentSetupManager } = useApiClient();
  const [opening, setOpening] = useState(false);
  const { company } = useSelector((state) => state.currentCompany);

  const provider = gateway ? t(`providers.${gateway}`, { defaultValue: gateway }) : null;
  const failure = errorMessage ?? (provider ? t('error', { provider }) : t('error_generic'));

  async function open() {
    setOpening(true);

    try {
      // Marked as a return, so coming back polls for the provider's answer instead of
      // showing the banner that sent the owner there.
      // `returnTo` names the step to come back to when it is not the page the button is on.
      const back = returnTo ?? window.location.href;
      const returnUrl = company?.id ? withPaymentReturn(back, company.id) : back;
      const { data: response, success } = await CompanyPaymentSetupManager.createOnboardingLink(returnUrl);

      // Stays loading while the page leaves: a second click would mint another link.
      if (success && response?.data?.url) {
        return window.location.assign(response.data.url);
      }

      setOpening(false);

      // A refusal the owner can fix (a postal code Stripe rejects, a return address outside
      // the partner's dashboard) arrives as a field error, not a title.
      return toast.error(firstApiError(response) ?? failure);
    } catch {
      setOpening(false);

      return toast.error(failure);
    }
  }

  return (
    <Button type={type} className={className} loading={opening} onClick={open} data-testid="hosted-onboarding-button">
      {children ?? (provider ? t('text', { provider }) : t('text_generic'))}
    </Button>
  );
}
